import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Layout from "../common/Layout";
import { leadImageFor } from "../assets/utils/productImages";
import { usePageMeta } from "../common/usePageMeta";
import { track } from "../features/analytics/tracker";

import NewsletterSignup from "../common/NewsletterSignup";
import MailingListPopup from "../common/MailingListPopup";
import { addToCart } from "../features/cart/cart";
import { useBoxes } from "../assets/utils/useBoxes";
import { getStock, getStockStatus, getStartingPrice, lowStockLabel } from "../assets/utils/productUtils";

const fadeStagger = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
const item = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.25 } } };

/* --- Helpers --- */
const isPublicVisible = (p) => p?.enabled !== false;
function isAvailableNow(p) {
  if (p?.inStock) return true;
  return getStockStatus(p) === "AVAILABLE";
}

/* --- Image Resolver --- */
const PLACEHOLDER_IMG = `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 3'><rect width='100%' height='100%' fill='%23eef3f9'/></svg>`;

function BoxCard({ box }) {
  const [addState, setAddState] = useState("idle");

  const handleAdd = () => {
    if (addState !== "idle") return;
    setAddState("adding");
    track("box_add_to_cart", { id: box.id, name: box.name, price: box.price });
    addToCart({
      id:     `box-${box.id}`,
      baseId: `box-${box.id}`,
      name:   `${box.name}${box.grams ? ` (${box.grams})` : ""}`,
      price:  box.price,
      qty:    1,
      grams:  0,
      image:  box.image,
    });
    setTimeout(() => setAddState("done"), 300);
    setTimeout(() => setAddState("idle"), 2200);
  };

  const saving = box.originalPrice
    ? (box.originalPrice - box.price).toFixed(2)
    : null;

  return (
    <motion.div
      className="taster-bag-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="taster-bag-media" style={{ backgroundImage: `url(${box.image})` }}>
        <div className="taster-bag-media-overlay" />
        {saving && <div className="taster-bag-save-pill">Save £{saving}</div>}
        {box.badges?.length > 0 && (
          <div className="taster-bag-badges">
            {box.badges.map(b => <span key={b} className="taster-bag-badge">{b}</span>)}
          </div>
        )}
      </div>

      <div className="taster-bag-body">
        <div className="taster-bag-top">
          <div>
            <h2 className="taster-bag-name">{box.name}</h2>
            {box.tagline && <p className="taster-bag-tagline">{box.tagline}</p>}
          </div>
          <div className="taster-bag-pricing">
            <span className="taster-bag-price">£{box.price.toFixed(2)}</span>
            {box.originalPrice && (
              <span className="taster-bag-was">was £{box.originalPrice.toFixed(2)}</span>
            )}
          </div>
        </div>

        {box.description && <p className="taster-bag-desc">{box.description}</p>}

        {box.contents?.length > 0 && (
          <div className="taster-bag-contents">
            <span className="taster-bag-contents-label">Typically includes:</span>
            {box.contents.map(c => (
              <span key={c} className="taster-bag-content-pill">{c}</span>
            ))}
          </div>
        )}

        <div className="taster-bag-footer">
          <NextDeliveryBadge />
          <button
            className={`btn taster-bag-btn ${addState === "done" ? "btn-secondary" : "btn-primary"}`}
            onClick={handleAdd}
            disabled={addState === "adding"}
          >
            {addState === "adding"
              ? "Adding…"
              : addState === "done"
              ? "✓ Added!"
              : `Add to Cart - £${box.price.toFixed(2)}`}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function BoxesSection({ boxes }) {
  if (!boxes.length) return null;
  const [featured, ...rest] = boxes;

  return (
    <>
      <section className="browse-section" style={{ marginBottom: "var(--s-4)" }}>
        <BoxCard box={featured} />
      </section>

      {rest.length > 0 && (
        <section className="browse-section">
          <p className="eyebrow" style={{ marginBottom: "var(--s-3)" }}>More selections</p>
          {rest.map((box, i) => (
            <div
              key={box.id}
              style={{ marginBottom: "var(--s-4)" }}
              className={i % 2 === 1 ? "bag-card-alt" : ""}
            >
              <BoxCard box={box} />
            </div>
          ))}
        </section>
      )}

      <div className="browse-section browse-divider">
        <span>Or browse individual products</span>
      </div>
    </>
  );
}

// In BrowseProductsPage.jsx - replace the hardcoded FOMO_MESSAGES and FomoBanner

const STATIC_FOMO = [
  "🌱 Harvested this morning - delivering across Essex today",
  "⏰ Order before 10am for same-morning delivery",
  "🐞 Zero pesticides - protected by ladybirds, not chemicals",
  "📍 Grown in Hockley · Delivered within 3 miles",
  "⏱️ From our farm to your door in under 2 hours",
  "🌿 No cold storage. No food miles. No compromise.",
];

function FomoBanner({ boxes = [] }) {
  const messages = STATIC_FOMO;

  const [idx, setIdx] = useState(0);

  // Reset idx if messages array length changes (e.g. boxes load in after mount)
  useEffect(() => { setIdx(0); }, [messages.length]);

  useEffect(() => {
    const id = setInterval(() => setIdx(i => (i + 1) % messages.length), 6000);
    return () => clearInterval(id);
  }, [messages.length]);

  return (
    <div className="fomo-banner" role="status" aria-live="polite">
      <motion.span
        key={idx}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.35 }}
        style={{ display: "block" }}
      >
        {messages[idx]}
      </motion.span>
    </div>
  );
}

/* --- Next delivery countdown --- */
function NextDeliveryBadge() {
  const getNext = () => {
    const now = new Date();
    const h = now.getHours();
    const m = now.getMinutes();
    const totalMins = h * 60 + m;
    // Morning window: 10:30 – 13:00 (cut-off 10:00 = 600 mins)
    // Evening window: 17:30 – 19:00 (cut-off 17:00 = 1020 mins)
    if (totalMins < 600) {
      const minsLeft = 600 - totalMins;
      return { label: "Morning delivery", when: `today ${h < 10 ? "10:30am" : "10:30am"}`, urgent: minsLeft < 90 };
    }
    if (totalMins < 1020) {
      const minsLeft = 1020 - totalMins;
      return { label: "Evening delivery", when: "today from 5:30pm", urgent: minsLeft < 90 };
    }
    return { label: "Tomorrow morning", when: "from 10:30am", urgent: false };
  };
  const [info, setInfo] = useState(getNext());
  useEffect(() => {
    const id = setInterval(() => setInfo(getNext()), 60000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className={`next-delivery-badge ${info.urgent ? "is-urgent" : ""}`}>
      {info.urgent ? "⚡" : "🚚"} {info.label} - {info.when}
      {info.urgent && <strong> · Order now!</strong>}
    </span>
  );
}

/* ================================================================
   MAIN PAGE
================================================================ */
export default function BrowseProductsPage() {
  usePageMeta({ title: 'Shop Fresh Herbs, Salads & Microgreens | TearDrop Hydro', description: "Browse today's harvest: basil, salad leaves, microgreens and more - grown in Hockley, harvested to order and delivered across Essex.", path: '/browse-products' });
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const [query, setQuery] = useState(params.get("q") || "");
  const [sort, setSort] = useState(params.get("sort") || "popular");
  const [onlyAvailable, setOnlyAvailable] = useState(params.get("available") === "1");
  const category = params.get("category") || "all";

  const [err, setErr] = useState("");
  const [products, setProducts] = useState([]);
  const aliveRef = useRef(true);
  const [loading, setLoading] = useState(true);

  const { boxes } = useBoxes();

  useEffect(() => {
    aliveRef.current = true;
    (async () => {
      try {
        const res = await fetch("https://teardrophydro.co.uk/garageFarm/products");
        const data = await res.json();
        if (aliveRef.current) {
          setProducts(Array.isArray(data) ? data : []);
          setLoading(false);
        }
      } catch {
        setErr("Couldn't load products.");
      }
    })();
    return () => { aliveRef.current = false; };
  }, []);

  // How far people actually scroll through the catalogue before leaving —
  // same milestone pattern used on the homepage.
  useEffect(() => {
    const fired = new Set();
    const milestones = [10, 25, 50, 75, 90, 100];
    const onScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const total    = document.documentElement.scrollHeight;
      const pct      = Math.round((scrolled / total) * 100);
      for (const m of milestones) {
        if (pct >= m && !fired.has(m)) {
          fired.add(m);
          track("browse_scroll_depth", { pct: m });
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const visible = useMemo(() => {
    let list = products.filter(isPublicVisible);
    if (category !== "all") list = list.filter((p) => (p.category || "").toLowerCase() === category);
    if (onlyAvailable) {
      list = list.filter(p => isAvailableNow(p) || getStock(p) < 0);
    }
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => [p.name, p.description].filter(Boolean).join(" ").toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      const statusA = getStockStatus(a);
      const statusB = getStockStatus(b);
      const rank = { "AVAILABLE": 0, "SOLD_OUT": 1, "COMING_SOON": 2 };
      if (rank[statusA] !== rank[statusB]) return rank[statusA] - rank[statusB];
      if (sort === "price-asc") return (Number(a.price) || 0) - (Number(b.price) || 0);
      if (sort === "price-desc") return (Number(b.price) || 0) - (Number(a.price) || 0);
      if (sort === "name") return String(a.name || "").localeCompare(String(b.name || ""));
      return 0;
    });
    return list;
  }, [products, category, onlyAvailable, query, sort]);



  // Only show taster bag on 'all' category view
const visibleBoxes = useMemo(() => {
  if (category === "all") return boxes;

  // Get the names of products visible in this category
  const visibleProductNames = products
    .filter(isPublicVisible)
    .filter(p => (p.category || "").toLowerCase() === category)
    .map(p => (p.name || "").toLowerCase());

  // Show a box if any of its contents match a visible product name
  return boxes.filter(box =>
    (box.contents || []).some(content =>
      visibleProductNames.some(name =>
        name.includes(content.toLowerCase()) ||
        content.toLowerCase().includes(name)
      )
    )
  );
}, [boxes, category, products]);

  return (
    <Layout>
      {/* FOMO banner - always visible */}
      <FomoBanner boxes={boxes} />

      <div className="container section">
        <section className="browse-section">
          <h1 className="h2" style={{ marginBottom: "var(--s-2)" }}>Today's Harvest</h1>
          <p className="muted" style={{ margin: "0 0 var(--s-4)", fontSize: "var(--fs-14)" }}>
            Picked to order and delivered the same day across Hockley, Rayleigh &amp; Rochford.
          </p>
          <div className="toolbar">
            <div className="pill-row">
              {["all", ...new Set(products.filter(isPublicVisible).map(p => p.category?.toLowerCase()).filter(Boolean))].map((c) => (
                <button key={c} className={`pill ${category === c ? "is-active" : ""}`} onClick={() => {
                  track("browse_category_change", { category: c });
                  const next = new URLSearchParams(params);
                  next.set("category", c);
                  setParams(next);
                }}>
                  {c.charAt(0).toUpperCase() + c.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* NEW */}
        {visibleBoxes.length > 0 && <BoxesSection boxes={visibleBoxes} />}

        <section className="browse-section">
          {loading ? (
            <div className="products">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="card" style={{ pointerEvents: "none" }}>
                  <div className="skeleton" />
                  <div className="content" style={{ display: "grid", gap: "var(--s-2)" }}>
                    <div className="skeleton" style={{ height: 18, borderRadius: 6, width: "60%" }} />
                    <div className="skeleton" style={{ height: 14, borderRadius: 6, width: "85%" }} />
                    <div className="skeleton" style={{ height: 14, borderRadius: 6, width: "40%" }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <motion.div className="products" variants={fadeStagger} initial="hidden" animate="show">
              {visible.map((p) => {
                const status = getStockStatus(p);
                const isAvailable = status === "AVAILABLE";
                const isSoon = status === "COMING_SOON";
                const isSoldOut = status === "SOLD_OUT";
                const img = leadImageFor(p) || PLACEHOLDER_IMG;
                const start = getStartingPrice(p);
                const lowStock = lowStockLabel(p);

                return (
                  <motion.article
                    key={p.id}
                    variants={item}
                    className={`card ${isSoldOut ? "is-out" : ""} ${isSoon ? "is-soon" : ""}`}
                    onClick={() => {
                      track("browse_product_click", { productId: p.id, name: p.name, category: p.category, status });
                      if (isSoon || isSoldOut) {
                        navigate(`/notify?item=${encodeURIComponent(p.name)}&status=${status}`);
                      } else {
                        navigate(`/product/${p.id}`);
                      }
                    }}
                  >
                    <div className="media" style={{ backgroundImage: `url(${img})`, position: 'relative' }}>
                      {(isSoon || isSoldOut) && (
                        <div className={`sold-out-overlay ${isSoon ? "is-soon" : ""}`}>
                          <span className={`badge ${isSoon ? "badge-soon" : "badge-out"}`}>
                            {isSoon ? "Coming Soon" : "Sold Out"}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="content">
                      <h3 className="h4">{p.name}</h3>
                      <p className="muted small">{p.short || p.description}</p>
                      <div className="meta">
                        <span className="price">
                          <span className="small muted">from </span>
                          £{start.price}
                          <span className="small muted"> ({start.grams}g)</span>
                        </span>

                        {isAvailable ? (
                          <span className={lowStock ? "pill-available pill-low" : "pill-available"}>{lowStock || "Available"}</span>
                        ) : isSoon ? (
                          <span className="waitlist-trigger">🌱 Join Waitlist</span>
                        ) : (
                          <span className="waitlist-trigger">🔔 Notify Me</span>
                        )}
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </motion.div>
          )}
        </section>

        <section className="section" data-track-key="newsletter">
          <div className="container">
            <NewsletterSignup />
          </div>
        </section>
      </div>
      <MailingListPopup />
    </Layout>
  );
}